import { ContentCopy, FilterAlt, MyLocation, Refresh, Update, ViewInAr } from "@mui/icons-material";
import {
    Box,
    Divider,
    IconButton,
    List,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { OctreeDebugNode, OctreeDebugNodeKind } from "@novorender/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    renderActions,
    selectDebugIsolatedNodeId,
    selectDebugNoSplitNodeIds,
    selectDebugSelectedNodeIds,
    selectDebugShowNodeFrames,
} from "features/render/renderSlice";
import useFlyTo from "hooks/useFlyTo";
import { useToggle } from "hooks/useToggle";

import { OctreeTreeNode } from "./octreeTreeNode";

const AUTO_REFRESH_INTERVAL_MS = 1000;

function collectSubtreeIds(roots: readonly OctreeDebugNode[], targetId: string): Set<string> | null {
    for (const root of roots) {
        const stack: OctreeDebugNode[] = [root];
        while (stack.length) {
            const node = stack.pop()!;
            if (node.id === targetId) {
                const ids = new Set<string>();
                const inner: OctreeDebugNode[] = [node];
                while (inner.length) {
                    const n = inner.pop()!;
                    ids.add(n.id);
                    for (const child of n.children) {
                        inner.push(child);
                    }
                }
                return ids;
            }
            for (const child of node.children) {
                stack.push(child);
            }
        }
    }
    return null;
}

function findNode(
    roots: readonly OctreeDebugNode[],
    id: string,
): { node: OctreeDebugNode; ancestors: string[] } | undefined {
    for (const root of roots) {
        const stack: { node: OctreeDebugNode; ancestors: string[] }[] = [{ node: root, ancestors: [] }];
        while (stack.length) {
            const { node, ancestors } = stack.pop()!;
            if (node.id === id) {
                return { node, ancestors };
            }
            for (const child of node.children) {
                stack.push({ node: child, ancestors: [...ancestors, node.id] });
            }
        }
    }
    return undefined;
}

export function OctreeDebugContent() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const flyTo = useFlyTo();
    const {
        state: { view },
    } = useExplorerGlobals();

    const showFrames = useAppSelector(selectDebugShowNodeFrames);
    const selectedNodeIds = useAppSelector(selectDebugSelectedNodeIds);
    const selectedId = selectedNodeIds[0];
    const noSplitNodeIds = useAppSelector(selectDebugNoSplitNodeIds);
    const noSplitIds = useMemo(() => new Set(noSplitNodeIds), [noSplitNodeIds]);
    const isolatedNodeId = useAppSelector(selectDebugIsolatedNodeId);

    const [tree, setTree] = useState<readonly OctreeDebugNode[]>([]);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [autoRefresh, toggleAutoRefresh] = useToggle(false);
    const [selectedKind, setSelectedKind] = useState<OctreeDebugNodeKind>("triangles");

    const seededKind = useRef<OctreeDebugNodeKind | undefined>(undefined);

    const refresh = useCallback(() => {
        if (!view) {
            return;
        }
        const nodes = view.getOctreeNodeTree(selectedKind);
        setTree(nodes);
        const kindChanged = seededKind.current !== selectedKind;
        seededKind.current = selectedKind;
        setExpanded((prev) => (prev.size > 0 && !kindChanged ? prev : new Set(nodes.map((n) => n.id))));
    }, [view, selectedKind]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!autoRefresh) {
            return;
        }
        const id = setInterval(refresh, AUTO_REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [autoRefresh, refresh]);

    const selected = useMemo(() => (selectedId ? findNode(tree, selectedId) : undefined), [tree, selectedId]);

    const isolatedSubtreeIds = useMemo(
        () => (isolatedNodeId ? collectSubtreeIds(tree, isolatedNodeId) : null),
        [tree, isolatedNodeId],
    );

    useEffect(() => {
        if (selected?.ancestors.length) {
            setExpanded((prev) => {
                const next = new Set(prev);
                for (const id of selected.ancestors) {
                    next.add(id);
                }
                return next;
            });
        }
    }, [selected]);

    const toggleNode = useCallback((id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const selectNode = useCallback(
        (node: OctreeDebugNode) => {
            dispatch(renderActions.setDebugSelectedNodeIds([node.id]));
        },
        [dispatch],
    );

    const toggleNoSplit = useCallback(
        (id: string) => {
            dispatch(renderActions.toggleDebugNoSplitNodeId(id));
        },
        [dispatch],
    );

    const selectedNode = selected?.node;

    const copyNodeJson = useCallback(() => {
        if (!selectedNode) {
            return;
        }
        const { children, ...rest } = selectedNode;
        const parentId = selected!.ancestors.at(-1) ?? null;
        const json = JSON.stringify({ ...rest, parentId, children: children.map((c) => c.id) }, null, 2);
        void navigator.clipboard.writeText(json);
    }, [selectedNode, selected]);

    return (
        <Box display="flex" flexDirection="column" height={1} overflow="hidden">
            <Box px={1} pt={1} pb={0.5} display="flex" alignItems="center" gap={0.5}>
                <Select
                    size="small"
                    value={selectedKind}
                    onChange={(e) => setSelectedKind(e.target.value as OctreeDebugNodeKind)}
                    sx={{ fontSize: "0.75rem", height: 28, flex: 1 }}
                >
                    {(["triangles", "terrain", "lines", "points", "documents"] as OctreeDebugNodeKind[]).map((kind) => (
                        <MenuItem key={kind} value={kind} dense>
                            {kind}
                        </MenuItem>
                    ))}
                </Select>
                <Tooltip title={t("autoRefresh")}>
                    <IconButton
                        size="small"
                        color={autoRefresh ? "primary" : "default"}
                        onClick={() => toggleAutoRefresh()}
                    >
                        <Update fontSize="small" />
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={refresh} disabled={autoRefresh}>
                    <Refresh fontSize="small" />
                </IconButton>
                <Tooltip title={t("showNodeFrames")}>
                    <IconButton
                        size="small"
                        color={showFrames ? "primary" : "default"}
                        onClick={() => dispatch(renderActions.setDebugShowNodeFrames(!showFrames))}
                    >
                        <ViewInAr fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Divider />
            {selectedNode && (
                <>
                    <Box px={1} py={0.5} maxHeight={220} overflow="auto">
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                            <Typography variant="subtitle2">{t("selectedNode")}</Typography>
                            <Box display="flex" gap={0.5}>
                                <Tooltip title={t("copyJson")}>
                                    <IconButton size="small" aria-label={t("copyJson")} onClick={copyNodeJson}>
                                        <ContentCopy fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t("flyTo")}>
                                    <IconButton
                                        size="small"
                                        aria-label={t("flyTo")}
                                        onClick={() => {
                                            void flyTo({
                                                sphere: {
                                                    center: [...selectedNode.center] as [number, number, number],
                                                    radius: selectedNode.radius,
                                                },
                                            });
                                        }}
                                    >
                                        <MyLocation fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t("isolate")}>
                                    <IconButton
                                        size="small"
                                        aria-label={t("isolate")}
                                        color={isolatedNodeId === selectedNode.id ? "warning" : "default"}
                                        onClick={() =>
                                            dispatch(
                                                renderActions.setDebugIsolatedNodeId(
                                                    isolatedNodeId === selectedNode.id ? undefined : selectedNode.id,
                                                ),
                                            )
                                        }
                                    >
                                        <FilterAlt fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <NodeProperties node={selectedNode} />
                    </Box>
                    <Divider />
                </>
            )}
            <Box flex="1 1 auto" overflow="auto">
                {tree.length === 0 ? (
                    <Box p={2}>
                        <Typography color="textSecondary">{t("noOctreeNodes")}</Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {tree.map((node) => (
                            <OctreeTreeNode
                                key={node.id}
                                node={node}
                                expanded={expanded}
                                selectedId={selectedId}
                                noSplitIds={noSplitIds}
                                isolatedSubtreeIds={isolatedSubtreeIds}
                                onToggle={toggleNode}
                                onSelect={selectNode}
                                onToggleNoSplit={toggleNoSplit}
                            />
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
}

export function NodeProperties({ node }: { node: OctreeDebugNode }) {
    const { t } = useTranslation();
    const fmt = (n: number) => (Number.isInteger(n) ? n.toString() : n.toFixed(2));
    const rows: [string, string][] = [
        ["id", node.id || "(root)"],
        ["level", node.level.toString()],
        ["state", node.state],
        ["visibility", node.visibility],
        ["childIndex", node.childIndex.toString()],
        ["childMask", node.childMask.toString(2).padStart(8, "0")],
        ["projectedSize", fmt(node.projectedSize)],
        ["radius", fmt(node.radius)],
        ["scale", fmt(node.scale)],
        ["tolerance", `${(2 ** node.tolerance).toPrecision(3)} m (2^${node.tolerance})`],
        ["gpuBytes", node.gpuBytes.toFixed(0)],
        ["offset", node.offset.map(fmt).join(", ")],
        ["hasGeometry", String(node.hasGeometry)],
        ["renderedPrimitives", node.renderedPrimitives.toLocaleString()],
        ["center", node.center.map(fmt).join(", ")],
        ["min", node.bounds.min.map(fmt).join(", ")],
        ["max", node.bounds.max.map(fmt).join(", ")],
    ];

    return (
        <Table size="small" sx={{ "& td": { px: 0.5, py: 0.25, border: 0 } }}>
            <TableBody>
                {rows.map(([key, value]) => (
                    <TableRow key={key}>
                        <TableCell>
                            <Typography variant="caption" color="textSecondary">
                                {key}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                                {value}
                            </Typography>
                            {key === "id" && node.id && (
                                <Tooltip title={t("copyNodeId")}>
                                    <IconButton
                                        size="small"
                                        aria-label={t("copyNodeId")}
                                        onClick={() => void navigator.clipboard.writeText(node.id)}
                                        sx={{ p: 0.25, ml: 0.5 }}
                                    >
                                        <ContentCopy sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
